import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { makeStyles } from '@fluentui/react-components';
import { loginRequest } from '../../config/msalConfig';
import { htsLogos } from '../../themes/htsTheme';
import { authBlockRemaining } from '../../services/authBlockState';

/**
 * Unified HTS login standard: hts-platform/docs/login-screen-standard.md
 *
 * Implementation note: this page intentionally avoids Fluent's `<Card>` and
 * `<Button>` primitives. Their internal layouts (CardBody wrapper with its own
 * padding, button icon-slot sizing) were eating the 40px card padding and
 * shrinking the Microsoft logo, so the rendered page didn't match the PP /
 * MC / Batman canonical. A plain `<div>` + native `<button>` + native `<h1>` /
 * `<p>` gives us exact control over padding, sizing, and the MS icon — same
 * approach Batman already uses (see apps/web/src/components/hts-shell/LoginPage.tsx).
 *
 * All colors go through the CSS-var system so the canonical navy stays in sync
 * with hts-platform/Brand-Kit if the brand refreshes.
 */
const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--hts-primary-navy)', // unified: navy page + white card everywhere
    // Own the top safe area (native iPad shell, contentInset 'never') —
    // this page renders outside Layout, so nothing else clears the status
    // pill. --hts-safe-top is the native shell's real status-bar height
    // (halved); env() covers browsers. paddingTop listed AFTER the padding
    // shorthand so it isn't reset (portal device-test lesson).
    padding: '24px',
    paddingTop: 'calc(24px + max(env(safe-area-inset-top, 0px), var(--hts-safe-top, 0px)))',
    paddingBottom: '72px', // clear the fixed footer
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    padding: '40px',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF', // card stays white in both modes (login standard)
    boxShadow: '0 10px 30px rgba(13, 33, 82, 0.08)', // --hts-shadow-login
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    height: '120px',
    width: 'auto',
    objectFit: 'contain',
    display: 'block',
    marginBottom: '16px',
  },
  title: {
    color: 'var(--hts-primary-navy)',
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1.2,
    textAlign: 'center',
    margin: 0,
    marginBottom: '8px',
  },
  subtitle: {
    color: 'var(--hts-ink-secondary)',
    fontSize: '14px',
    lineHeight: 1.5,
    textAlign: 'center',
    margin: 0,
    marginBottom: '24px',
  },
  button: {
    width: '100%',
    minHeight: '54px',
    padding: '15px 28px',
    backgroundColor: 'var(--hts-primary-navy)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 120ms ease-out',
    ':hover': {
      backgroundColor: 'var(--hts-primary-navy-hover)',
    },
    ':focus': {
      outline: '2px solid var(--hts-focus-ring-color)',
      outlineOffset: '2px',
    },
    ':disabled': {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '2px 16px',
    backgroundColor: 'var(--hts-footer-bg)', // darker than page navy so the bar always stands out
    color: 'rgba(255, 255, 255, 0.7)',
    padding: '10px 20px',
    fontSize: '11px',
    lineHeight: 1.4,
    textAlign: 'center',
  },
  footerBuild: {
    color: 'rgba(255, 255, 255, 0.7)', // same grey as the copyright line
  },
  holdNote: {
    color: 'var(--hts-ink-secondary)',
    fontSize: '12px',
    lineHeight: 1.5,
    textAlign: 'center',
    margin: 0,
    marginTop: '12px',
  },
});

// Canonical Microsoft four-square logo — pixel-perfect pre-rasterized PNGs.
// A 20px inline SVG lands its inner cross on fractional device pixels (gap edges
// at 9.50/10.49 CSS px), so at fractional display scales (125% Windows scaling,
// browser zoom) each edge rounds independently and the cross renders lopsided.
// These PNGs (public/brand/ms-logo-*.png) are authored on integer, symmetric
// grids (equal squares, equal gaps, gap ~= 5% of width — the same proportions as
// Microsoft_Logo_SVG.svg) at each exact device size, so the logo is
// pixel-straight at every pixel ratio. Real files (not data URIs) because
// Chromium ignores density descriptors on data-URI srcset candidates.
const MS_LOGO_SRCSET =
  '/brand/ms-logo-20.png 1x, /brand/ms-logo-25.png 1.25x, /brand/ms-logo-30.png 1.5x, ' +
  '/brand/ms-logo-40.png 2x, /brand/ms-logo-50.png 2.5x, /brand/ms-logo-60.png 3x';

function MicrosoftLogo({ size = 20 }: { size?: number }) {
  return (
    <img
      src="/brand/ms-logo-40.png"
      srcSet={MS_LOGO_SRCSET}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      style={{ width: size, height: size, flexShrink: 0, display: 'block' }}
    />
  );
}

export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const { instance } = useMsal();

  // Retry hold-down after a security-layer block (see services/authBlockState).
  // Ticks every second only while a hold-down is/was active; 0 once expired.
  const [retryHoldMs, setRetryHoldMs] = useState(0);
  useEffect(() => {
    const update = () => setRetryHoldMs(authBlockRemaining());
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, []);
  const heldDown = retryHoldMs > 0;

  const handleSignIn = async () => {
    if (authBlockRemaining() > 0) return; // guard races with the ticking state
    try {
      // Full-page redirect to Microsoft and back. Same as PP/MC/Batman.
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <img
          src={htsLogos.primary}
          alt="Harris Technology Services"
          className={styles.logo}
        />
        <h1 className={styles.title}>Site Survey</h1>
        <p className={styles.subtitle}>
          Field site walkthroughs, device mapping, and survey documentation
        </p>
        <button
          type="button"
          onClick={handleSignIn}
          className={styles.button}
          disabled={heldDown}
          // autoFocus: regression #2 — the page boots with the focus target on
          // the h1 ("Site Survey"), which then draws the dark-mode focus ring
          // (#38C7F0) around the title text. Putting focus on the Sign In
          // button (the only interactive element) is the contract-correct
          // focus target.
          autoFocus
        >
          <MicrosoftLogo />
          {heldDown
            ? `Sign-in blocked — retry in ${Math.ceil(retryHoldMs / 1000)}s`
            : 'Sign In with Microsoft'}
        </button>
        {heldDown && (
          <p className={styles.holdNote}>
            A network security layer blocked the last sign-in attempt. Waiting keeps
            immediate retries from re-blocking everyone sharing this network.
          </p>
        )}
      </div>
      <footer className={styles.footer}>
        <span>
          Unauthorized access prohibited. &copy; {new Date().getFullYear()} Harris Technology Services.
        </span>
        <span className={styles.footerBuild}>
          v{__APP_VERSION__}
          {typeof window !== 'undefined' && window.location.hostname === 'localhost' ? ' — LOCAL MODE' : ''}
        </span>
      </footer>
    </div>
  );
};

export default LoginPage;
